import { cx } from '@emotion/css';
import { CalendarDate, parseDate } from '@internationalized/date';
import { DateRangePicker, FormControl, View } from '@zinc/ui';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { type DateRange as AriaDateRange, I18nProvider } from 'react-aria-components';
import { DateRange } from 'tm3-core/api.dtos';
import { cache } from 'tm3-core/cache';

export interface IProps {
  className?: string;
  disabled?: boolean;
  errorText?: string;
  label: string;
  submitted?: boolean;
  value: DateRange | undefined;
  dataTestId?: string;
  min?: string | moment.Moment | undefined;
  max?: string | moment.Moment | undefined;
  onChange: (value: DateRange | null) => void;
}

const FieldDateRangePicker: React.FC<IProps> = (props: IProps) => {
  const {
    value,
    min,
    max,
    errorText,
    submitted,
    dLabel,
    label,
    className,
    onChange,
    dataTestId,
    ...other
  } = props;

  const computedLabel = useMemo(
    () => (dLabel ? (cache.dynamicLabels.GetLabel(dLabel)?.text ?? label ?? '') : label),
    [label, dLabel]
  );

  const toCalendarDate = useCallback(
    (input: string | moment.Moment | undefined): CalendarDate | undefined => {
      if (!input) return undefined;
      const m = moment.isMoment(input) ? input : moment(input);
      if (!m.isValid()) return undefined;
      const utcM = m.utc();

      //CalendarDate months are 1 based, moment months are 0 based
      return new CalendarDate(utcM.year(), utcM.month() + 1, utcM.date());
    },
    []
  );

  const formattedMin = useMemo(() => toCalendarDate(min), [min, toCalendarDate]);
  const formattedMax = useMemo(() => toCalendarDate(max), [max, toCalendarDate]);

  //Converts BZ DateRange to react aria DateRange
  const dateValue = useMemo(() => {
    if (!value || !value.from || !value.to) return undefined;
    const start = parseDate(moment(value.from).format('YYYY-MM-DD'));
    const end = parseDate(moment(value.to).format('YYYY-MM-DD'));
    return { start, end };
  }, [value]);

  //Converts react aria DateRange to BZ DateRange
  const onChangeDateRange = useCallback(
    (e: AriaDateRange) => {
      const from = e.start ? moment.utc(e.start.toDate()).toISOString() : null;
      const to = e.end ? moment.utc(e.end.toDate()).toISOString() : null;
      const range = from && to ? new DateRange({ from, to }) : null;
      onChange?.(range);
    },
    [onChange]
  );

  const _className = useMemo(() => {
    return cx(
      className,
      errorText && !submitted ? 'border !border-orange-300' : 'border',
      errorText && submitted ? 'border !border-red-300' : 'border'
    );
  }, [className, errorText, submitted]);

  return (
    <View paddingBottom={2}>
      <FormControl hasError={submitted && !!errorText}>
        {computedLabel && <FormControl.Label>{computedLabel}</FormControl.Label>}
        <I18nProvider locale={window.localStorage.locale || 'en'}>
          <DateRangePicker
            {...other}
            minValue={formattedMin}
            maxValue={formattedMax}
            value={dateValue}
            onChange={onChangeDateRange}
            className={_className}
            attributes={{
              'data-testid': dataTestId,
              placeholderValue: parseDate(moment().startOf('year').format('YYYY-MM-DD'))
            }}
          />
        </I18nProvider>
        <FormControl.Error>{errorText}</FormControl.Error>
      </FormControl>
    </View>
  );
};

FieldDateRangePicker.displayName = 'FieldDateRangePicker';
export default FieldDateRangePicker;
